import { useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { socket } from "../../../common/socket";
import { fetchChatById } from "../../../slices/chat/thunk";
import {
  clearChatError,
  clearUnread,
  clearPendingForChat,
  hydrateChatFromCache,
  setActiveChatId,
} from "../../../slices/chat/reducer";
import { showToast } from "../../../slices/toast/reducer";

export const useChatSelection = (
  selectedChatId,
  currentChat,
  currentUserId,
  messageDrafts,
  normalizeMessages,
  currentMessagesRef
) => {
  const dispatch = useDispatch();
  const isFetchingRef = useRef(false);

  const handleSelectGroup = useCallback((chat) => {
    const chatId = chat.chatRequestId || chat._id || chat.groupId;

    if (!chatId) {
      dispatch(showToast({ type: "error", msg: "Invalid chat selected" }));
      return null;
    }

    // Don't re-fetch if already selected
    if (selectedChatId === chatId && currentChat?._id === chatId) {
      console.log("🔄 [CHAT] Chat already selected, skipping fetch");
      return chatId;
    }

    // Prevent multiple simultaneous fetches
    if (isFetchingRef.current) {
      console.log("⏳ [CHAT] Already fetching chat, please wait");
      return null;
    }

    // Stop typing in previous chat
    if (selectedChatId && selectedChatId !== chatId) {
      socket.emit("typing:stop", { chatId: selectedChatId });
    }

    isFetchingRef.current = true;

    console.log("🎯 [CHAT] Selecting group:", {
      chatId: chatId,
      chatName: chat.name,
      previousChat: selectedChatId
    });

    // Clear errors and unread counts
    dispatch(clearChatError());
    dispatch(clearUnread(chatId));
    
    try {
      const authUser = JSON.parse(localStorage.getItem("authUser"));
      const myId = authUser?.user?._id || authUser?._id;
      if (myId) {
        dispatch(clearPendingForChat({ chatId, userId: myId }));
      }
    } catch (error) {
      console.warn("Error clearing pending:", error);
    }

    // Set active chat immediately for better UX
    dispatch(setActiveChatId(chatId));

    // Try to load from cache first
    dispatch(hydrateChatFromCache(chatId));

    // Fetch fresh data from API
    dispatch(fetchChatById({ chatId }))
      .then((result) => {
        isFetchingRef.current = false;

        if (result.payload?.messages) {
          const normalizedMessages = normalizeMessages(result.payload.messages);
          currentMessagesRef.current = normalizedMessages;
          console.log("✅ [CHAT] Messages loaded:", normalizedMessages.length);
        }

        // Mark messages as read via socket
        socket.emit("markMessagesAsRead", { chatId });
        
        // Join the chat room
        if (socket.connected) {
          socket.emit("joinChat", { chatId });
        }

        console.log("✅ [CHAT] Successfully loaded chat:", chatId);
      })
      .catch((err) => {
        isFetchingRef.current = false;
        console.error("❌ [CHAT] Failed to load chat:", err);
        dispatch(showToast({ 
          type: "error", 
          msg: err.message || "Failed to load chat messages" 
        }));
      });

    return chatId;
  }, [
    selectedChatId, 
    currentChat?._id, 
    dispatch, 
    messageDrafts, 
    currentUserId, 
    normalizeMessages, 
    currentMessagesRef
  ]);

  return { handleSelectGroup, isFetchingRef };
};